import { useState, useEffect } from 'react';
import { getAccesCaisses, setVisibiliteCaisses, setActivationCaisses, getTotauxCaisseNoire, getTotauxCaisse3mt } from '../api';

/*
 * Les caisses appartiennent à l'équipe : c'est l'argent des joueurs.
 * Cette enveloppe montre à chacun ce qu'il peut voir, et permet au trésorier
 * ou au coach de l'équipe de choisir ce que la direction du club en voit.
 */
const euros = n => `${(Number(n) || 0).toFixed(2).replace('.', ',')} €`;
const CHOIX = [
  { v: 'aucune', titre: 'Rien',          aide: "Les caisses restent entre les membres de l'équipe." },
  { v: 'totaux', titre: 'Les totaux',    aide: 'La direction voit les montants globaux, pas le détail par joueur.' },
  { v: 'detail', titre: 'Le détail',     aide: 'La direction voit tout, en lecture seule.' },
];
const carte = { background: 'var(--card, #161b22)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px', marginBottom: 14 };

function Totaux({ type }) {
  const [t, setT] = useState(null);
  useEffect(() => { (type === 'noire' ? getTotauxCaisseNoire() : getTotauxCaisse3mt()).then(setT).catch(() => setT({})); }, [type]);
  if (!t) return null;
  const lignes = type === 'noire'
    ? [['Gages', t.amendes], ['Encaissé', t.encaisse], ['Reste dû', t.reste_du]]
    : [['Entrées', t.total_entrees], ['Dépenses', t.total_depenses], ['Solde', t.solde]];
  return (
    <div style={carte}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {lignes.map(([l, m]) => (
          <div key={l} style={{ flex: '1 1 110px' }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1 }}>{l}</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{euros(m)}</div>
          </div>))}
      </div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10 }}>
        Totaux partagés par l'équipe. Le détail par joueur reste entre ses membres.</div>
    </div>
  );
}

export default function CaissesGarde({ type, children }) {
  const [a, setA] = useState(null);
  const [erreur, setErreur] = useState('');
  useEffect(() => { getAccesCaisses().then(setA).catch(e => setErreur(e.message)); }, []);
  if (erreur) return <div className="page"><div style={carte}>{erreur}</div></div>;
  if (!a) return <div className="page" style={{ color: 'var(--muted)', padding: 20 }}>Chargement…</div>;

  const activer = async actives => {
    if (actives && !window.confirm("Activer les caisses de l'équipe ?\n\nElles démarrent vierges : c'est à l'équipe de fixer ses règles. Pour une équipe de jeunes, pensez à en parler aux parents avant de demander de l'argent aux joueurs.")) return;
    try { await setActivationCaisses(actives); setA(await getAccesCaisses()); } catch (e) { setErreur(e.message); }
  };
  if (a.actives === false) return (
    <div className="page">
      <div style={carte}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Caisses non utilisées par cette équipe</div>
        <div style={{ fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.55 }}>
          Pour une équipe de jeunes, la caisse noire et la 3ème mi-temps sont désactivées par défaut :
          demander de l'argent à des enfants n'a pas sa place sans l'accord des familles.
          {a.peut_activer ? " En tant que coach, vous pouvez les activer si l'équipe le souhaite vraiment." : " Seul le coach de l'équipe peut les activer."}
        </div>
        {a.peut_activer && <button type="button" className="btn" style={{ marginTop: 12 }} onClick={() => activer(true)}>Activer les caisses de l'équipe</button>}
      </div>
    </div>);

  if (a.acces === 'aucun') return (
    <div className="page">
      <div style={carte}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>🔒 Caisses réservées à l'équipe</div>
        <div style={{ fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.55 }}>
          Les caisses appartiennent à l'équipe : seuls ses joueurs et son encadrement les voient.
          Le trésorier ou le coach de l'équipe peut choisir d'en montrer les totaux ou le détail à la direction du club.
        </div>
      </div>
    </div>);

  if (a.acces === 'totaux') return <div className="page"><Totaux type={type} /></div>;

  const reglage = a.peut_regler && (
    <div className="page" style={{ paddingTop: 0 }}>
      <div style={carte}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>🔒 Ce que la direction du club voit des caisses de l'équipe</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {CHOIX.map(c => (
            <button key={c.v} type="button" aria-pressed={a.visibilite_direction === c.v}
              onClick={async () => { try { const r = await setVisibiliteCaisses(c.v); setA(x => ({ ...x, visibilite_direction: r.visibilite_direction })); } catch (e) { setErreur(e.message); } }}
              style={{ flex: '1 1 120px', textAlign: 'left', cursor: 'pointer', borderRadius: 9, padding: '8px 10px',
                       background: a.visibilite_direction === c.v ? 'rgba(230,57,70,.12)' : 'transparent',
                       border: `1px solid ${a.visibilite_direction === c.v ? 'var(--accent)' : 'var(--border)'}`, color: 'var(--text)' }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{c.titre}</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{c.aide}</div>
            </button>))}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 8 }}>
          C'est l'argent des joueurs : ce réglage appartient à l'équipe, la direction ne peut pas le modifier.</div>
        {a.peut_activer && (
          <button type="button" onClick={() => { if (window.confirm("Désactiver les caisses de l'équipe ? Elles disparaîtront du menu des joueurs ; rien n'est effacé.")) activer(false); }}
                  style={{ marginTop: 10, background: 'none', border: 'none', color: 'var(--muted)', fontSize: 12, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>
            Désactiver les caisses de l'équipe</button>)}
      </div>
    </div>);

  return (
    <>
      {type === 'noire' && a.bareme_exemple && a.acces === 'complet' && (
        <div className="page" style={{ paddingBottom: 0 }}>
          <div style={{ ...carte, borderColor: 'rgba(234,179,8,.45)', background: 'rgba(234,179,8,.07)', fontSize: 13, lineHeight: 1.5 }}>
            📋 <b>Barème d'exemple</b> : ces gages et ces défis sont un point de départ. Le trésorier peut les modifier
            ou les supprimer pour fixer les règles de l'équipe ; ce message disparaît dès la première modification.
          </div>
        </div>)}
      {a.acces === 'detail' && (
        <div className="page" style={{ paddingBottom: 0 }}>
          <div style={{ ...carte, fontSize: 13, color: 'var(--muted)' }}>
            👁️ Lecture seule : l'équipe partage le détail de ses caisses avec la direction du club.</div>
        </div>)}
      {children}
      {reglage}
    </>
  );
}
